import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";

/* Ant Design */
import { Alert, Button, Input, Modal, Skeleton } from "antd";
import { PageHeader, Section, SectionCard, useFlatPage } from "../components/ui";
import { IconInfo } from "../components/ui/icons";
import notify from "../utils/notify";

const ProfileSettingsPage = () => {
  useFlatPage();
  const navigate = useNavigate();
  const { authData } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [error, setError] = useState("");

  // profile fields
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName]   = useState("");
  const [email, setEmail]         = useState("");

  // server-required fields for PUT
  const [isActive, setIsActive]   = useState(true);
  const [roleId, setRoleId]       = useState(""); // current role id to send back

  // password fields
  const [oldPassword, setOldPassword]                 = useState("");
  const [newPassword, setNewPassword]                 = useState("");
  const [confirmNewPassword, setConfirmNewPassword]   = useState("");

  const changingPassword = useMemo(
    () => !!(oldPassword || newPassword || confirmNewPassword),
    [oldPassword, newPassword, confirmNewPassword]
  );

  /* Load profile + role id */
  useEffect(() => {
    let mounted = true;
    (async () => {
      setError("");
      try {
        // 1) basic profile
        const prof = await axiosInstance.get("/user/profile");
        if (!mounted) return;
        setFirstName(prof?.data?.firstName || "");
        setLastName(prof?.data?.lastName || "");
        setEmail(prof?.data?.email || "");
        setIsActive(!!prof?.data?.isActive);

        // 2) current role id (needed by PUT endpoint)
        const userId = authData?.userId;
        if (userId) {
          const rolesRes = await axiosInstance.get(`/user/${userId}/Roles`);
          const roles = rolesRes?.data?.roles || [];
          // pick first role (or keep empty if none)
          setRoleId(roles[0]?.id || "");
        }
      } catch (e) {
        if (!mounted) return;
        setError("Error fetching profile data.");
        console.error(e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [authData?.userId]);

  const validate = () => {
    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      notify.warning("First name, last name, and email are required.");
      return false;
    }
    if (changingPassword) {
      if (!oldPassword)      { notify.error("Enter your current password to change password."); return false; }
      if (!newPassword || !confirmNewPassword) {
        notify.error("Fill both new password fields."); return false;
      }
      if (newPassword !== confirmNewPassword) {
        notify.error("New password and confirmation do not match."); return false;
      }
      if (newPassword.length < 8) {
        notify.error("New password must be at least 8 characters."); return false;
      }
    }
    return true;
  };

  const onResetPasswords = () => {
    setOldPassword("");
    setNewPassword("");
    setConfirmNewPassword("");
  };

  const onReload = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axiosInstance.get("/user/profile");
      setFirstName(res?.data?.firstName || "");
      setLastName(res?.data?.lastName || "");
      setEmail(res?.data?.email || "");
      setIsActive(!!res?.data?.isActive);
      // keep roleId as-is
      onResetPasswords();
      notify.success("Profile reloaded.");
    } catch (e) {
      setError("Error reloading profile data.");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async () => {
    if (!validate()) return;

    setSaving(true);
    setError("");
    try {
      const payload = {
        firstName: firstName.trim(),
        lastName:  lastName.trim(),
        isActive,       // required by backend
        roleId,         // required by backend (unchanged role)
        ...(oldPassword && { oldPassword, newPassword }),
      };

      const userId = authData?.userId;
      const res = await axiosInstance.put(`/user/User/${userId}`, payload);

      if (res.status === 200) {
        notify.success({
          title: "Profile updated",
          description: "Your profile changes have been saved.",
        });
        onResetPasswords();
      } else {
        throw new Error("Failed to update profile.");
      }
    } catch (e) {
      const msg = e?.response?.data?.message || "Error updating profile. Please try again.";
      setError(msg);
      notify.error({ message: "Update failed", description: msg });
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const field = (label, node, hint) => (
    <div className="hms-field">
      <label>{label}</label>
      {node}
      {hint && <div className="hms-cell-sub" style={{ marginTop: 4 }}>{hint}</div>}
    </div>
  );

  return (
    <div className="hms-page ps-page">
      <PageHeader
        actions={
          <>
            <Button icon={<IconInfo />} onClick={() => setShowInfo(true)}>
              Guide
            </Button>
            <Button onClick={onReload}>Reload</Button>
            <Button type="primary" loading={saving} onClick={onSubmit}>
              Save changes
            </Button>
          </>
        }
      />

      {error && <Alert style={{ marginBottom: 12 }} type="error" title="Operation failed" description={error} showIcon />}

      {loading ? (
        <SectionCard>
          <Skeleton active paragraph={{ rows: 6 }} />
        </SectionCard>
      ) : (
        <div className="hms-stack" style={{ maxWidth: 760 }}>
          <SectionCard title="Basic information">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              {field(
                "First name",
                <Input placeholder="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} disabled={saving} />
              )}
              {field(
                "Last name",
                <Input placeholder="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} disabled={saving} />
              )}
            </div>
            {field("Email", <Input value={email} readOnly disabled />, "Your email is managed by the system and can't be changed here.")}
          </SectionCard>

          <SectionCard
            title="Change password"
            extra={
              <Button onClick={onResetPasswords} disabled={saving || !changingPassword}>
                Clear fields
              </Button>
            }
          >
            {field(
              "Current password",
              <Input.Password
                placeholder="Enter your current password to change it"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                disabled={saving}
                autoComplete="current-password"
              />
            )}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              {field(
                "New password",
                <Input.Password
                  placeholder="Leave blank to keep your password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={saving}
                  autoComplete="new-password"
                />,
                "At least 8 characters."
              )}
              {field(
                "Confirm new password",
                <Input.Password
                  placeholder="Repeat the new password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  disabled={saving}
                  autoComplete="new-password"
                />
              )}
            </div>
          </SectionCard>

          <div className="hms-form-actions" style={{ justifyContent: "space-between" }}>
            <Button onClick={() => navigate("/login")} disabled={saving}>
              Sign out and log in again
            </Button>
            <Button type="primary" loading={saving} onClick={onSubmit}>
              Save changes
            </Button>
          </div>
        </div>
      )}

      <Modal
        title="Profile settings guide"
        open={showInfo}
        onCancel={() => setShowInfo(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setShowInfo(false)}>
            Got it
          </Button>,
        ]}
        width={520}
        destroyOnHidden
      >
        <p className="hms-modal-note">Update your name and, if you want, change your password. Your email is read-only here.</p>
        <Section title="Tips">
          <ul style={{ paddingLeft: 20, margin: 0 }}>
            <li>Reload pulls the latest profile from the server.</li>
            <li>To change your password, give your current password and the new one twice.</li>
            <li>Passwords must be at least 8 characters.</li>
          </ul>
        </Section>
      </Modal>
    </div>
  );
};

export default ProfileSettingsPage;
